// Component Studio API client (MOD-17, config-tier + versiyonlu).
//
// Base `/api/v1/admin/components`. Mutasyonlar (POST/PUT/DELETE/publish/archive/
// restore) `components.manage` policy'siyle korunur; palette + list + detail +
// versions yalnız kimlik ister ([Authorize]). X-Project-Id http.ts'te merkezî
// enjekte edilir. İş kuralları BE'de: COMPONENT_SCHEMA_INVALID / _TEMPLATE_INVALID
// (422) · COMPONENT_NAME_TAKEN / _IN_USE (409, error.data.usageCount) · _DELETED (422).

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  normalizeComponentStatus,
  type ComponentCreateInput,
  type ComponentDefinitionDtoWire,
  type ComponentDetail,
  type ComponentListItem,
  type ComponentListItemDtoWire,
  type ComponentListQuery,
  type ComponentPaletteItem,
  type ComponentPaletteItemDtoWire,
  type ComponentUpdateInput,
  type ComponentUsage,
  type ComponentVersion,
  type ComponentVersionDtoWire,
  type CreateComponentRequest,
  type UpdateComponentRequest,
} from '@features/component-studio/types';

/**
 * K-36 usages wire tipi — generated openapi'de henüz olmayabilir (BE B-son).
 * `pageId` alanı BE'de `pageId` veya `id` olarak dönebilir; defensive map'lenir.
 */
interface ComponentUsageWire {
  pageId?: string | null;
  id?: string | null;
  title?: string | null;
  status?: string | null;
}

const BASE = '/api/v1/admin/components';

function mapListItem(d: ComponentListItemDtoWire): ComponentListItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    category: d.category ?? '',
    status: normalizeComponentStatus(d.status),
    blockKey: d.blockKey ?? '',
    updatedAt: d.updatedAt ?? '',
  };
}

function mapDetail(d: ComponentDefinitionDtoWire): ComponentDetail {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    category: d.category ?? '',
    propsSchemaJson: d.propsSchemaJson ?? '',
    templateHtml: d.templateHtml ?? '',
    componentJson: d.componentJson ?? '',
    defaultStyleJson: d.defaultStyleJson ?? '',
    status: normalizeComponentStatus(d.status),
    blockKey: d.blockKey ?? '',
    updatedAt: d.updatedAt ?? '',
  };
}

function mapPaletteItem(d: ComponentPaletteItemDtoWire): ComponentPaletteItem {
  return {
    id: d.id ?? '',
    blockKey: d.blockKey ?? '',
    name: d.name ?? '',
    category: d.category ?? '',
    propsSchemaJson: d.propsSchemaJson ?? '',
    templateHtml: d.templateHtml ?? '',
    componentJson: d.componentJson ?? '',
    defaultStyleJson: d.defaultStyleJson ?? '',
  };
}

function mapVersion(d: ComponentVersionDtoWire): ComponentVersion {
  return {
    id: d.id ?? '',
    componentId: d.componentId ?? '',
    versionNumber: d.versionNumber ?? 0,
    name: d.name ?? '',
    changeNote: d.changeNote ?? '',
    authorEmail: d.authorEmail ?? '',
    templateHtml: d.templateHtml ?? '',
    componentJson: d.componentJson ?? '',
    createdAt: d.createdAt ?? '',
  };
}

export const componentsApi = {
  /** Admin liste (tüm durumlar) — kategori/durum daraltmalı. */
  async list(query: ComponentListQuery = {}): Promise<ComponentListItem[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 200 };
    if (query.category) params.category = query.category;
    if (query.status) params.status = query.status;
    const res = await http.get<ActionResponse<ComponentListItemDtoWire>>(BASE, { params });
    return unwrapList(res).items.map(mapListItem);
  },

  /** Sayfa-yapıcı paleti — PUBLISHED-only (herhangi bir kimlikli kullanıcı). */
  async palette(): Promise<ComponentPaletteItem[]> {
    const res = await http.get<ActionResponse<ComponentPaletteItemDtoWire>>(`${BASE}/palette`);
    return unwrapList(res).items.map(mapPaletteItem);
  },

  async get(id: string): Promise<ComponentDetail> {
    return mapDetail(unwrap(await http.get<ActionResponse<ComponentDefinitionDtoWire>>(`${BASE}/${id}`)));
  },

  async versions(id: string): Promise<ComponentVersion[]> {
    const res = await http.get<ActionResponse<ComponentVersionDtoWire>>(`${BASE}/${id}/versions`);
    return unwrapList(res).items.map(mapVersion);
  },

  /**
   * K-36 — bileşeni kullanan yayındaki sayfalar (cmp_ bloğu). Manuel-wire (uç
   * generated api'de yoksa); 404'te çağıran query error'a düşer → panel gizlenir.
   */
  async usages(id: string): Promise<ComponentUsage[]> {
    const res = await http.get<ActionResponse<ComponentUsageWire>>(`${BASE}/${id}/usages`);
    return unwrapList(res).items.map((d) => ({
      pageId: d.pageId ?? d.id ?? '',
      title: d.title ?? '',
      status: d.status ?? '',
    }));
  },

  /** POST → tam ComponentDefinitionDto döner. */
  async create(input: ComponentCreateInput): Promise<ComponentDetail> {
    const body: CreateComponentRequest = {
      name: input.name,
      category: input.category ?? '',
      componentJson: input.componentJson,
    };
    return mapDetail(unwrap(await http.post<ActionResponse<ComponentDefinitionDtoWire>>(BASE, body)));
  },

  /** PUT → güncellenmiş ComponentDefinitionDto döner. */
  async update(id: string, input: ComponentUpdateInput): Promise<ComponentDetail> {
    const body: UpdateComponentRequest = {
      name: input.name,
      category: input.category,
      propsSchemaJson: input.propsSchemaJson,
      templateHtml: input.templateHtml,
      componentJson: input.componentJson,
      defaultStyleJson: input.defaultStyleJson ?? null,
      changeNote: input.changeNote ?? null,
    };
    return mapDetail(unwrap(await http.put<ActionResponse<ComponentDefinitionDtoWire>>(`${BASE}/${id}`, body)));
  },

  /** DELETE → 409 COMPONENT_IN_USE (error.data.usageCount) kullanımdaysa. */
  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  async publish(id: string): Promise<void> {
    unwrap(await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null));
  },

  async archive(id: string): Promise<void> {
    unwrap(await http.post<ActionResponse<boolean>>(`${BASE}/${id}/archive`, null));
  },

  async restoreVersion(id: string, versionNumber: number): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/versions/${versionNumber}/restore`, null),
    );
  },
};
